import { useState, useEffect, lazy, Suspense, Component } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import { FontLink } from "./components/UI";
import { T } from "./utils";
import { supabase } from "./supabase";
import Login from "./pages/Login";
import Register from "./pages/Register";
import QRCheckIn from "./pages/QRCheckIn";
import { Spinner } from "./components/UI";
import { manifests, pageComponents } from "./frame/registry";
import { buildMenu } from "./frame/buildMenu";
import { mfaStep } from "./frame/mfa";
import MfaScreen from "./frame/pages/MfaScreen";
import PendingApproval from "./frame/pages/PendingApproval";
import { reportRenderError } from "./monitoring/sentry";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error("App error:", error, info);
    // Tell Sentry (error monitoring) about the crash, so we hear about it even when
    // the member does not report it. Silent when Sentry is not switched on.
    reportRenderError(error, info?.componentStack);
    // Stale chunk after a new deploy — reload once to fetch fresh assets
    const isChunkError = error?.name === "ChunkLoadError" || /Loading chunk \d+ failed/.test(error?.message || "");
    if (isChunkError && !sessionStorage.getItem("chunk_reload")) {
      sessionStorage.setItem("chunk_reload", "1");
      window.location.reload();
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight:"100vh", background:T.cream, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, fontFamily:"'DM Sans',sans-serif", padding:32 }}>
          <div style={{ fontSize:48 }}>⚠️</div>
          <div style={{ fontSize:20, fontWeight:700, color:T.navy }}>Something went wrong</div>
          <div style={{ fontSize:14, color:T.grey600, maxWidth:400, textAlign:"center" }}>{this.state.error?.message || "An unexpected error occurred."}</div>
          <button onClick={() => { sessionStorage.removeItem("chunk_reload"); this.setState({ hasError:false, error:null }); window.location.reload(); }}
            style={{ padding:"10px 24px", borderRadius:8, background:T.navy, color:"#fff", border:"none", cursor:"pointer", fontSize:14, fontWeight:600 }}>
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Every page now comes from the module registry (src/frame/registry.js).
// Vision Builder stays here because it is a first-run gate, not a menu page.
const VisionBuilder = lazy(() => import("./pages/VisionBuilder"));

// All page components, looked up by page id (e.g. "events" → Events page)
const PAGES = pageComponents(manifests);
const HQ_ROLES = ["superuser", "admin"];

function Shell() {
  const { user, profile, loading, signOut } = useAuth();
  // The current page lives in the URL (/people, /events …) so a refresh stays on the
  // same page, links can be shared, and the browser Back button works. "/" = Dashboard.
  const pageFromUrl = () => window.location.pathname.replace(/^\/+|\/+$/g, "");
  const [page, setPageState]      = useState(pageFromUrl);
  const [pageHistory, setPageHistory] = useState([]);

  // Switch page AND record it in the browser's history
  const setPage = (newPage) => {
    setPageState(newPage);
    const path = `/${newPage}`;
    if (window.location.pathname !== path) window.history.pushState({ page: newPage }, "", path);
  };

  // Browser Back / Forward buttons
  useEffect(() => {
    const onPop = () => {
      setPageState(pageFromUrl());
      setPageHistory(prev => prev.slice(0, -1));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [showTour, setShowTour]   = useState(false);
  const [builderDone, setBuilderDone] = useState(true); // assume done until checked
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [notifications,    setNotifications]    = useState([]);
  const [showNotifs,       setShowNotifs]        = useState(false);
  const [enabledIds,       setEnabledIds]        = useState(null);  // modules HQ has switched on
  const [leadsChapter,     setLeadsChapter]      = useState(false); // does this person lead a chapter?
  const [mfa,              setMfa]               = useState(null);  // 'none' | 'enroll' | 'challenge'

  const isHq = HQ_ROLES.includes(profile?.role);

  // Which modules are switched on (Leadership → Modules). If the table cannot be
  // read yet (before the frame is installed) show everything rather than nothing.
  useEffect(() => {
    if (!profile?.id) return;
    supabase.from("app_modules").select("id, enabled").then(({ data, error }) => {
      if (error || !data) setEnabledIds(new Set(manifests.map(m => m.id)));
      else setEnabledIds(new Set(data.filter(m => m.enabled).map(m => m.id)));
    });
    supabase.from("chapter_roles").select("id").eq("profile_id", profile.id).eq("active", true)
      .in("role", ["temporary_leader", "leader", "lead_mta"]).limit(1)
      .then(({ data }) => setLeadsChapter(!!(data && data.length)));
  }, [profile?.id]);

  // Two-step login check: HQ must have verified an authenticator code this session
  const checkMfa = async () => {
    if (!profile) return;
    if (!HQ_ROLES.includes(profile.role)) { setMfa("none"); return; }
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    const { data: factors } = await supabase.auth.mfa.listFactors();
    setMfa(mfaStep({
      role: profile.role,
      currentLevel: aal?.currentLevel,
      nextLevel: aal?.nextLevel,
      hasFactor: (factors?.totp || []).some(f => f.status === "verified"),
    }));
  };
  useEffect(() => { checkMfa(); }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load + subscribe to this user's notifications
  useEffect(() => {
    if (!profile?.id) return;
    const load = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", profile.id)
        .order("created_at", { ascending: false })
        .limit(30);
      if (data) setNotifications(data);
    };
    load();
    const channel = supabase
      .channel(`notifs-${profile.id}`)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"notifications", filter:`user_id=eq.${profile.id}` },
        (payload) => setNotifications(prev => [payload.new, ...prev]))
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [profile?.id]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (!unreadIds.length) return;
    await supabase.from("notifications").update({ read: true }).in("id", unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const navigateTo = (newPage) => {
    setPageHistory(prev => [...prev, page]);
    setPage(newPage);
  };

  // In-app "← Back" button: same as the browser's Back (the popstate handler above updates the page)
  const navigateBack = () => window.history.back();

  // Listen for navigate events from Dashboard quick actions
  useEffect(() => {
    const handler = (e) => navigateTo(e.detail);
    window.addEventListener("navigate", handler);
    return () => window.removeEventListener("navigate", handler);
  }, [page]);

  // Show welcome tour for first-time users
  useEffect(() => {
    if (profile && !profile.tour_completed) setShowTour(true);
  }, [profile]);

  // Check Vision Builder completion for superusers
  useEffect(() => {
    if (profile?.role === "superuser") {
      if (user?.email === "bookeryuri@gmail.com" || user?.email === "pastor.bookeryuri@gmail.com") return; // always exempt — builder already filled
      supabase.from("system_config").select("value").eq("key","vision_wizard_completed").single()
        .then(({ data }) => { if (data?.value === "false") setBuilderDone(false); });
    }
  }, [profile]);

  const dismissTour = async () => {
    setShowTour(false);
    if (profile) await supabase.from('profiles').update({ tour_completed:true }).eq('id',profile.id);
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", background:T.cream, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <FontLink/>
      <img src="/logo192.png" alt="CCN" style={{ width:88, height:88, objectFit:"contain" }}/>
      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, color:T.navy, fontWeight:700 }}>Clarion Call Network</div>
      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:13, color:T.gold, fontWeight:600 }}>Raising Kings and Priests</div>
      <Spinner label="Loading your workspace..."/>
    </div>
  );

  if (!user || !profile) return <Login/>;

  // New sign-ups wait for HQ approval (declined/suspended see a message too)
  if (profile.status && profile.status !== "approved") return <PendingApproval profile={profile} onSignOut={signOut}/>;

  // HQ must complete two-step login before anything else
  if (mfa === null || enabledIds === null) return <Spinner label="Loading your workspace..."/>;
  if (mfa !== "none") return <MfaScreen step={mfa} onVerified={checkMfa} onSignOut={signOut}/>;

  // Vision Builder gate — first-run only (superuser must complete before seeing anything)
  if (!builderDone) return (
    <Suspense fallback={<Spinner label="Loading..."/>}>
      <VisionBuilder onComplete={() => setBuilderDone(true)}/>
    </Suspense>
  );

  const openVisionBuilderModal = () => setShowBuilderModal(true);
  const closeVisionBuilderModal = () => setShowBuilderModal(false);

  // The sidebar: built from module manifests, HQ's on/off switches and the person's role
  const menu = buildMenu(manifests, enabledIds, { ...profile, leads_chapter: leadsChapter });
  // Pages this person may open: everything in their menu, plus the few link-only pages
  const allowedPages = new Set(["aboutus", "groups", "profile", ...menu.flatMap(s => s.items.map(i => i.page))]);
  // Home page: HQ lands on the Dashboard, everyone else on Events
  const home = isHq ? "dashboard" : "events";
  const current = allowedPages.has(page) ? page : home;
  const CurrentPage = PAGES[current] || PAGES[home];

  // Count follow-up alerts (passed from data context in full app)
  const alertCounts = {};

  const roleColors = { superuser:T.red, finance:T.green, admin:T.blue, assistant_pastor:T.teal||'#0e7490', leader:T.amber, media:T.purple, member:T.grey500||'#6b7280' };
  const roleColor  = roleColors[profile.role] || T.navy;

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:T.grey50, fontFamily:"'DM Sans',sans-serif" }}>
      <FontLink/>

      {/* ── SIDEBAR ── */}
      <div style={{ width:230, background:T.navy, display:"flex", flexDirection:"column", position:"fixed", top:0, left:0, bottom:0, zIndex:100, boxShadow:"4px 0 20px rgba(0,0,0,0.15)" }}>

        {/* Logo */}
        <div style={{ padding:"20px 16px 16px", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
          <img src="/logo192.png" alt="CCN" style={{ width:44, height:44, objectFit:"contain", display:"block", margin:"0 auto 8px" }}/>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:13, fontWeight:700, color:T.white, textAlign:"center", lineHeight:1.3 }}>Clarion Call Network</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:11, fontWeight:600, color:T.goldLight, textAlign:"center", marginTop:2 }}>Raising Kings and Priests</div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:"12px 10px", overflowY:"auto" }}>
          {menu.map(({ section, items: sectionModules }) => {
            return (
              <div key={section}>
                {section && (
                  <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.25)", textTransform:"uppercase", letterSpacing:"1px", padding:"14px 12px 6px" }}>
                    {section}
                  </div>
                )}
                {sectionModules.map(m => {
                  const active = current === m.page;
                  const alert  = alertCounts[m.page];
                  return (
                    <div key={m.page} onClick={() => { setPage(m.page); setPageHistory([]); }}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:8, marginBottom:1, cursor:"pointer", background:active?"rgba(232,116,32,0.18)":"transparent", borderLeft:active?`3px solid ${T.orange}`:"3px solid transparent", transition:"all 0.15s" }}>
                      <span style={{ fontSize:14 }}>{m.icon}</span>
                      <span style={{ fontSize:13, fontWeight:active?700:400, color:active?T.white:"rgba(255,255,255,0.6)", flex:1 }}>{m.label}</span>
                      {alert && (
                        <span style={{ fontSize:10, fontWeight:700, background:T.red, color:"#fff", borderRadius:10, padding:"1px 6px" }}>{alert}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* About Us footer link */}
        <div style={{ padding:"4px 10px" }}>
          <div onClick={() => setPage("aboutus")}
            style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 12px", borderRadius:8, cursor:"pointer", background:page==="aboutus"?"rgba(232,116,32,0.18)":"transparent", transition:"all 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.07)"}
            onMouseLeave={e => e.currentTarget.style.background=page==="aboutus"?"rgba(232,116,32,0.18)":"transparent"}>
            <span style={{ fontSize:13 }}>✝️</span>
            <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>About CCN</span>
          </div>
        </div>

        {/* Notification bell */}
        <div style={{ padding:"8px 10px", borderTop:"1px solid rgba(255,255,255,0.08)" }}>
          <div onClick={() => { setShowNotifs(v => !v); if (!showNotifs) markAllRead(); }}
            style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:8, cursor:"pointer", background:showNotifs?"rgba(232,116,32,0.18)":"rgba(255,255,255,0.05)", borderLeft:showNotifs?`3px solid ${T.orange}`:"3px solid transparent", transition:"all 0.15s", position:"relative" }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.1)"}
            onMouseLeave={e => e.currentTarget.style.background=showNotifs?"rgba(232,116,32,0.18)":"rgba(255,255,255,0.05)"}>
            <span style={{ fontSize:14 }}>🔔</span>
            <span style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.8)", flex:1 }}>Notifications</span>
            {unreadCount > 0 && (
              <span style={{ fontSize:10, fontWeight:700, background:T.red, color:"#fff", borderRadius:10, padding:"1px 6px", minWidth:16, textAlign:"center" }}>{unreadCount}</span>
            )}
          </div>
        </div>

        {/* User info + logout */}
        <div style={{ padding:"14px 16px", borderTop:"1px solid rgba(255,255,255,0.08)" }}>
          <div onClick={() => setPage("myprofile")}
            style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, cursor:"pointer", borderRadius:8, padding:"4px 4px", transition:"background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.07)"}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}>
            <div style={{ width:32, height:32, borderRadius:"50%", background:roleColor, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff", flexShrink:0 }}>
              {(profile.full_name||"U").split(" ").map(x=>x[0]).join("").slice(0,2).toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:700, color:T.white, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{profile.full_name}</div>
              <div style={{ fontSize:10, color:roleColor, fontWeight:600, textTransform:"capitalize" }}>{profile.role} · My Profile</div>
            </div>
          </div>
          <button onClick={signOut}
            style={{ width:"100%", padding:"7px", borderRadius:7, border:"1px solid rgba(255,255,255,0.15)", background:"transparent", color:"rgba(255,255,255,0.5)", fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <main style={{ marginLeft:230, flex:1, padding:"32px 36px", minHeight:"100vh", maxWidth:"calc(100vw - 230px)", boxSizing:"border-box" }}>
        {pageHistory.length > 0 && (
          <button onClick={navigateBack}
            style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:20, padding:"7px 14px", borderRadius:8, border:`1px solid ${T.grey200}`, background:T.white, color:T.navy, fontSize:13, fontWeight:600, cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}
            onMouseEnter={e => e.currentTarget.style.background = T.grey200}
            onMouseLeave={e => e.currentTarget.style.background = T.grey100}>
            ← Back
          </button>
        )}
        <ErrorBoundary>
          <Suspense fallback={<Spinner label="Loading module..."/>}>
            <CurrentPage setPage={setPage} redoVisionBuilder={openVisionBuilderModal}/>
          </Suspense>
        </ErrorBoundary>
      </main>

      {/* ── NOTIFICATION PANEL ── */}
      {showNotifs && (
        <>
          <div onClick={() => setShowNotifs(false)} style={{ position:"fixed", inset:0, zIndex:199 }}/>
          <div style={{ position:"fixed", left:230, bottom:0, top:0, width:340, background:T.white, zIndex:200, boxShadow:"4px 0 24px rgba(0,0,0,0.15)", display:"flex", flexDirection:"column" }}>
            <div style={{ padding:"18px 20px 14px", borderBottom:`1px solid ${T.grey200}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:T.navy, fontFamily:"'Playfair Display',serif" }}>Notifications</div>
                {unreadCount > 0
                  ? <div style={{ fontSize:11, color:T.grey500, marginTop:2 }}>{unreadCount} unread</div>
                  : <div style={{ fontSize:11, color:T.grey400, marginTop:2 }}>All caught up</div>
                }
              </div>
              <button onClick={() => setShowNotifs(false)}
                style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:T.grey400, padding:"4px 8px" }}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:"auto" }}>
              {notifications.length === 0 ? (
                <div style={{ padding:"48px 24px", textAlign:"center", color:T.grey400 }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>🔔</div>
                  <div style={{ fontWeight:600, fontSize:14 }}>No notifications yet</div>
                  <div style={{ fontSize:12, marginTop:4 }}>Messages from leadership will appear here.</div>
                </div>
              ) : notifications.map(n => (
                <div key={n.id} style={{ padding:"14px 20px", borderBottom:`1px solid ${T.grey200}`, background:n.read ? T.grey50 : "rgba(96,165,250,0.08)" }}>
                  {!n.read && <div style={{ width:6, height:6, borderRadius:"50%", background:T.blue, display:"inline-block", marginRight:8, verticalAlign:"middle" }}/>}
                  <div style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:3, display:"inline" }}>{n.title}</div>
                  <div style={{ fontSize:13, color:T.grey600, lineHeight:1.6, marginTop:4 }}>{n.body}</div>
                  {n.link && (
                    <a href={n.link} target="_blank" rel="noreferrer"
                      style={{ fontSize:11, color:T.blue, fontWeight:600, marginTop:4, display:"inline-block" }}>Open link →</a>
                  )}
                  <div style={{ fontSize:10, color:T.grey300, marginTop:6 }}>
                    {n.created_at ? new Date(n.created_at).toLocaleDateString("en-KE",{ day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── VISION BUILDER MODAL (Update Vision) ── */}
      {showBuilderModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(15,32,68,0.85)", zIndex:9998, overflowY:"auto" }}>
          <div style={{ position:"relative", minHeight:"100vh", padding:"32px 24px" }}>
            <button onClick={closeVisionBuilderModal}
              style={{ position:"fixed", top:20, right:28, zIndex:9999, background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)", color:"#fff", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontSize:13, fontWeight:600 }}>
              ✕ Close
            </button>
            <Suspense fallback={<Spinner label="Loading..."/>}>
              <VisionBuilder onComplete={closeVisionBuilderModal}/>
            </Suspense>
          </div>
        </div>
      )}

      {/* ── WELCOME TOUR ── */}
      {showTour && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:T.white, border:`1px solid ${T.grey200}`, borderRadius:20, padding:"40px 48px", maxWidth:520, textAlign:"center", boxShadow:"0 24px 80px rgba(0,0,0,0.6)" }}>
            <img src="/logo192.png" alt="CCN" style={{ width:72, height:72, objectFit:"contain", marginBottom:16 }}/>
            <div style={{ fontSize:24, fontWeight:700, color:T.navy, fontFamily:"'Playfair Display',serif", marginBottom:8 }}>
              Welcome to Clarion Call Network, {profile.full_name?.split(" ")[0]}!
            </div>
            <div style={{ fontSize:14, color:T.grey600, lineHeight:1.8, marginBottom:24 }}>
              Christ to the Nations Outreach Ministry & Clarion Call Network management platform.
              Here's what you can do:
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:28, textAlign:"left" }}>
              {[
                { icon:"📖", label:"Bible Study & Prayer", desc:"Daily devotionals & prayer guides" },
                { icon:"💬", label:"Let's Talk", desc:"Team messaging & channels" },
                { icon:"📅", label:"Events", desc:"Church events & RSVP" },
                { icon:"🎙️", label:"Messages", desc:"Sermon recordings & notes" },
                { icon:"🎓", label:"Training Academy", desc:"Courses & certificates" },
              ].map(f => (
                <div key={f.label} style={{ display:"flex", gap:10, padding:"10px 12px", borderRadius:8, background:T.grey50 }}>
                  <span style={{ fontSize:20, flexShrink:0 }}>{f.icon}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:T.navy }}>{f.label}</div>
                    <div style={{ fontSize:11, color:T.grey400 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ fontSize:13, color:T.grey400, marginBottom:20 }}>
              Start by setting up your profile — add a photo and birthday!
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button onClick={async()=>{await dismissTour();setPage("myprofile");}}
                style={{ padding:"12px 24px", borderRadius:10, background:T.navy, color:"#fff", border:"none", cursor:"pointer", fontWeight:700, fontSize:14 }}>
                Set Up My Profile
              </button>
              <button onClick={dismissTour}
                style={{ padding:"12px 24px", borderRadius:10, background:T.white, color:T.grey600, border:"none", cursor:"pointer", fontWeight:600, fontSize:14 }}>
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  // Public pages — accessible without login
  if (window.location.pathname === "/register") return <Register/>;
  if (window.location.pathname === "/checkin")  return <QRCheckIn/>;

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Shell/>
      </AuthProvider>
    </ErrorBoundary>
  );
}
